import type {IHttpService, IPromise, IQService} from 'angular';
import {getRecordUrlId, getResponseToRejectedData, responseToData} from 'shared/utils/data-service-utils';
import type {PromiseBasedRecordEditationDataService} from './record-editation';
import type {Fond, Identified, Record, RecordEditation, RecordKind} from 'typings/portaro.be.types';
import {isDefined, uuid} from 'shared/utils/custom-utils';

export interface NewRecordEditationParams {
    kind: RecordKind,
    record?: Record,
    fond?: Fond | Identified<number>
}

export default class RecordEditationDataService implements PromiseBasedRecordEditationDataService {

    public static readonly serviceName = 'recordEditationDataService';

    /*@ngInject*/
    constructor(private $http: IHttpService,
                private $q: IQService,
                private CONTEXT_PATH: string) {
    }

    public createNewEditation(params: NewRecordEditationParams) {
        if (!params.kind) {
            throw new Error('kind is not defined');
        }
        const recordParam = params.record ? `&record=${getRecordUrlId(params.record)}` : '';
        const fondParam = params.fond ? `&fond=${params.fond.id}` : '';
        return this.doGetTo(null, `/new?kind=${params.kind}${recordParam}${fondParam}`);
    }

    public createField(editationId, code, position) {
        const positionParam = isDefined(position) ? `&position=${position}` : '';
        return this.doPutTo(editationId, `/fields/${uuid()}?code=${code}${positionParam}`);
    }

    public createSubfield(editationId, parentId, code) {
        return this.doPutTo(editationId, `/fields/${encodeURIComponent(parentId)}/${uuid()}?code=${encodeURIComponent(code)}`);
    }

    public deleteField(editationId, fieldIdPath) {
        return this.doDeleteTo(editationId, `/fields/${fieldIdPath}`);
    }

    public moveField(editationId, fieldIdPath, isDown) {
        return this.doPostTo(editationId, `/fields/${fieldIdPath}/move/${isDown ? 'down' : 'up'}`);
    }

    public setFieldValue(editationId, fieldIdPath, value) {
        return this.doPostTo(editationId, `/fields/${fieldIdPath}`, {value});
    }

    public clearFieldValue(editationId, fieldIdPath) {
        return this.doPostTo(editationId, `/fields/${fieldIdPath}/clear`);
    }

    public setFieldIndicators(editationId, fieldId, ind1, ind2) {
        return this.doPostTo(editationId, `/fields/${encodeURIComponent(fieldId)}/indicators`, {ind1, ind2});
    }

    public setFond(editationId, fondId) {
        return this.doPostTo(editationId, `/fond/${encodeURIComponent(fondId)}`);
    }

    public save(editationId) {
        return this.doPostTo(editationId, '/save');
    }

    public publish(editationId) {
        return this.doPostTo(editationId, '/publish');
    }

    public remove(editationId) {
        return this.doPostTo(editationId, '/delete');
    }

    private getEditationApiPath(editationId) {
        const editationIdParam: string = editationId ? `/${editationId}` : '';
        return `${this.CONTEXT_PATH}/api/record-editations${editationIdParam}`;
    }

    private doGetTo(editationId, pathAndParams: string): IPromise<RecordEditation> {
        return this.$http.get(this.getEditationApiPath(editationId) + pathAndParams)
                   .then(responseToData, getResponseToRejectedData(this.$q))
                   .then((responseData) => responseData as RecordEditation);
    }

    private doPostTo(editationId, pathAndParams: string, data?): IPromise<RecordEditation> {
        return this.$http.post(this.getEditationApiPath(editationId) + pathAndParams, data)
                   .then(responseToData, getResponseToRejectedData(this.$q))
                   .then((responseData) => responseData as RecordEditation);
    }

    private doPutTo(editationId, pathAndParams: string, data?): IPromise<RecordEditation> {
        return this.$http.put(this.getEditationApiPath(editationId) + pathAndParams, data)
                   .then(responseToData, getResponseToRejectedData(this.$q))
                   .then((responseData) => responseData as RecordEditation);
    }

    private doDeleteTo(editationId, pathAndParams: string): IPromise<RecordEditation> {
        return this.$http.delete(this.getEditationApiPath(editationId) + pathAndParams)
                   .then(responseToData, getResponseToRejectedData(this.$q))
                   .then((responseData) => responseData as RecordEditation);
    }
}
